import React, { useState } from 'react';
import { Camera, FileText, AlertCircle, Upload, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";

export default function IdentityVerification({ user, onVerificationComplete }) {
    const [selfie, setSelfie] = useState(null);
    const [document, setDocument] = useState(null);
    const [docType, setDocType] = useState('aadhaar');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            if (type === 'selfie') setSelfie(file);
            else setDocument(file);
        }
    };

    const handleVerify = async () => {
        if (!selfie || !document) {
            toast.error("Please upload both your Selfie and Document.");
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append('selfie', selfie);
        formData.append('document', document);
        formData.append('doc_type', docType);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_BASE}/verify/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });

            setResult(res.data);
            if (res.data.verified) {
                toast.success("Identity Verified Successfully!");
                if (onVerificationComplete) onVerificationComplete();
            } else {
                toast.error("Verification Failed: " + res.data.message);
            }
        } catch (err) {
            console.error(err);
            toast.error("Verification error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (user?.is_verified) {
        return (
            <div className="bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-800 rounded-xl p-6 text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center mb-4">
                    <ShieldCheck className="w-8 h-8 text-green-600 dark:text-green-300" />
                </div>
                <h3 className="text-xl font-bold text-green-800 dark:text-green-200 mb-2">Verified Identity</h3>
                <p className="text-green-600 dark:text-green-400">
                    Your identity has been verified on {new Date(user.verification_date).toLocaleDateString()}.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-xl p-4 flex gap-3 text-sm text-blue-800 dark:text-blue-200">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p>
                    Verify your identity to unlock premium features and build trust.
                    Upload a clear selfie and a photo of your Aadhaar Card or LinkedIn Profile.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Selfie Upload */}
                <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-6 text-center hover:border-brand-400 transition-colors">
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'selfie')}
                        className="hidden"
                        id="selfie-upload"
                    />
                    <label htmlFor="selfie-upload" className="cursor-pointer block">
                        {selfie ? (
                            <div className="relative">
                                <img
                                    src={URL.createObjectURL(selfie)}
                                    alt="Selfie"
                                    className="w-32 h-32 object-cover rounded-full mx-auto"
                                />
                                <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                    <p className="text-white text-xs">Change</p>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto">
                                    <Camera className="w-8 h-8 text-slate-400" />
                                </div>
                                <p className="font-medium text-slate-700 dark:text-slate-300">Upload Selfie</p>
                                <p className="text-xs text-slate-400">Clear face photo</p>
                            </div>
                        )}
                    </label>
                </div>

                {/* Document Upload */}
                <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-6 text-center hover:border-brand-400 transition-colors">
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'doc')}
                        className="hidden"
                        id="doc-upload"
                    />
                    <label htmlFor="doc-upload" className="cursor-pointer block">
                        {document ? (
                            <div className="relative">
                                <img
                                    src={URL.createObjectURL(document)}
                                    alt="Doc"
                                    className="w-full h-32 object-cover rounded-lg"
                                />
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto">
                                    <FileText className="w-8 h-8 text-slate-400" />
                                </div>
                                <p className="font-medium text-slate-700 dark:text-slate-300">Upload Document</p>
                                <select
                                    value={docType}
                                    onChange={(e) => setDocType(e.target.value)}
                                    onClick={(e) => e.stopPropagation()} // Prevent file dialog trigger
                                    className="text-xs p-1 border rounded bg-white dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 mt-2"
                                >
                                    <option value="aadhaar">Aadhaar Card</option>
                                    <option value="linkedin">LinkedIn Screenshot</option>
                                </select>
                            </div>
                        )}
                    </label>
                </div>
            </div>

            {loading ? (
                <div className="w-full py-4 flex flex-col items-center justify-center text-brand-600">
                    <Loader2 className="w-8 h-8 animate-spin mb-2" />
                    <p className="text-sm font-medium">Analyzing biometrics...</p>
                </div>
            ) : (
                <button
                    onClick={handleVerify}
                    disabled={!selfie || !document}
                    className="w-full py-3 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                    <Upload className="w-5 h-5" /> Verify Identity
                </button>
            )}

            {result && !result.verified && (
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-800 rounded-lg p-4 text-center">
                    <p className="text-red-600 font-bold mb-1">Verification Failed</p>
                    <p className="text-red-500 text-sm">{result.message}</p>
                </div>
            )}
        </div>
    );
}
